import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { Clock, Info } from 'lucide-react'
import { MainLayout } from '@/components/layout/MainLayout'
import { InvoiceApplicationDialog } from '@/components/setup/InvoiceApplicationDialog'
import { CompanyCreditCard, StandardFundingCard } from '@/components/payments/PaymentOptions'
import { useAuth } from '@/hooks/useAuth'
import { useGetBillingQuery } from '@/redux/marketerApi'

/**
 * The last step of brand onboarding: how the brand will pay, without asking for a card yet.
 * Standard funding needs no setup: when a deal is accepted Athios asks the brand to fund it
 * before paid work starts. Larger brands can apply for company credit (invoice billing) now or
 * later from Payments.
 */
const PaymentsSetup: React.FC = () => {
  const { marketer } = useAuth()
  const org = (marketer as any)?.org
  const { data: billing } = useGetBillingQuery()
  const [applyOpen, setApplyOpen] = useState(false)
  const app = billing?.application
  const invoiced = billing?.method === 'invoice'
  const pending = app?.status === 'pending'

  return (
    <MainLayout>
      <div className="container mx-auto max-w-6xl space-y-5 p-4 sm:p-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-wider text-[#3F7A0A]">
            Step 2 of 2 · Almost ready
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-foreground">
            You're ready to create with Athios
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            Create campaigns, shop athletes' content, and explore opportunities. You can set up
            payment when you're ready to confirm your first deal.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2">
          <StandardFundingCard compact recommended={!invoiced} />
          <CompanyCreditCard compact>
            {invoiced ? (
              <p className="rounded-xl bg-[#F4FBEA] px-4 py-3 text-sm text-foreground">
                <span className="font-semibold">Approved.</span> You pay by invoice, net{' '}
                {billing?.termsDays ?? 30}.
              </p>
            ) : pending ? (
              <p className="flex items-start gap-2 rounded-xl bg-[#FFF7E6] px-4 py-3 text-sm text-[#92400E]">
                <Clock className="mt-0.5 h-4 w-4 shrink-0" />
                Your application is being reviewed. We'll let you know, usually within two business
                days.
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  onClick={() => setApplyOpen(true)}
                  className="h-10 rounded-xl border border-border bg-card px-4 text-sm font-semibold text-foreground hover:bg-muted"
                >
                  Apply now
                </button>
                <span className="text-sm text-muted-foreground">or apply later from Payments</span>
              </div>
            )}
          </CompanyCreditCard>
        </div>

        <div className="flex gap-3 rounded-2xl border border-[#BFD7FB] bg-[#EFF5FF] px-4 py-3 text-sm">
          <Info className="mt-0.5 h-5 w-5 shrink-0 text-[#2563EB]" />
          <div>
            <p className="font-semibold text-foreground">
              When a deal is accepted, Athios will prompt you to fund it before paid work begins.
            </p>
            <p className="text-muted-foreground">
              You can manage payment options anytime in{' '}
              <Link
                to="/payments"
                className="font-medium text-[#2563EB] underline-offset-2 hover:underline"
              >
                Payments
              </Link>
              .
            </p>
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link
            to="/"
            className="inline-flex h-11 items-center rounded-xl bg-[#C0FD72] px-6 text-sm font-bold text-[#0F172A] hover:bg-[#B2F25E]"
          >
            Go to your dashboard
          </Link>
          <Link
            to="/campaign/create"
            className="inline-flex h-11 items-center rounded-xl border border-border bg-card px-6 text-sm font-semibold text-foreground hover:bg-muted"
          >
            Post your first opportunity
          </Link>
        </div>
      </div>

      <InvoiceApplicationDialog
        open={applyOpen}
        onOpenChange={setApplyOpen}
        defaultLegalName={org?.name}
        defaultContactName={(marketer as any)?.name}
        defaultEmail={(marketer as any)?.email}
      />
    </MainLayout>
  )
}

export default PaymentsSetup
